import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SearchInput, Text } from '@teals/ui-web';
import type { ModelDisplaySource } from '../utils/modelDisplay';
import {
  groupModelsByLab,
  matchesModelSearch,
  modelMetaLine,
} from '../utils/modelDisplay';

export type ModelPickerPin = {
  id: string;
  label: string;
  selected?: boolean;
};

type ModelPickerProps = {
  models: ModelDisplaySource[];
  query: string;
  onQueryChange: (value: string) => void;
  onSelect: (productId: string) => void;
  selectedId?: string;
  pinned?: ModelPickerPin[];
  onPinnedSelect?: (id: string) => void;
  listLabel?: string;
  placeholder?: string;
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: min(100%, 22rem);
`;

const List = styled.div`
  max-height: 20rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const LabLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 0.5rem 0.25rem 0.125rem;
`;

const Option = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  font-family: inherit;
  padding: 0.5rem 0.625rem;
  border-radius: 8px;
  border: 1px solid
    ${(p) => (p.$active ? 'var(--accent, #2d9a8f)' : 'transparent')};
  background: ${(p) =>
    p.$active ? 'var(--accent-subtle, rgba(45, 154, 143, 0.08))' : 'transparent'};
  color: var(--text-primary);
  cursor: pointer;

  &:hover {
    background: var(--bg-elevated, rgba(0, 0, 0, 0.03));
  }

  &:focus-visible {
    outline: 2px solid var(--accent, #2d9a8f);
    outline-offset: 1px;
  }
`;

const Name = styled.span`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
`;

const Meta = styled.span`
  display: block;
  margin-top: 0.125rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

function ModelOption({
  label,
  meta,
  active,
  onClick,
}: {
  label: string;
  meta?: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <Option
      type="button"
      role="option"
      aria-selected={Boolean(active)}
      $active={active}
      onClick={onClick}
    >
      <Name>{label}</Name>
      {meta ? <Meta>{meta}</Meta> : null}
    </Option>
  );
}

const ModelPicker: React.FC<ModelPickerProps> = ({
  models,
  query,
  onQueryChange,
  onSelect,
  selectedId,
  pinned = [],
  onPinnedSelect,
  listLabel = 'Models',
  placeholder = 'Search by name, id, lab, or route…',
}) => {
  const searching = Boolean(query.trim());
  const matches = useMemo(() => {
    if (!searching) return models;
    return models.filter((m) => matchesModelSearch(m, query));
  }, [models, query, searching]);
  const groups = useMemo(() => {
    if (searching) return [];
    const byModel = new Map<string, ModelDisplaySource[]>();
    for (const model of matches) {
      const key = model.modelKey || model.productId;
      const ways = byModel.get(key) || [];
      ways.push(model);
      byModel.set(key, ways);
    }
    const multi = [...byModel.values()].some((ways) => ways.length > 1);
    if (!multi) return groupModelsByLab(matches);
    return [...byModel.entries()].map(([key, ways]) => ({
      lab: key,
      label: ways[0].displayName || key,
      models: ways,
    }));
  }, [matches, searching]);

  return (
    <Body>
      <SearchInput
        value={query}
        onChange={onQueryChange}
        placeholder={placeholder}
        debounceMs={0}
      />
      <List role="listbox" aria-label={listLabel}>
        {pinned.map((pin) => (
          <ModelOption
            key={pin.id}
            label={pin.label}
            active={pin.selected}
            onClick={() => onPinnedSelect?.(pin.id)}
          />
        ))}
        {matches.length === 0 ? (
          <Text
            variant="body2"
            style={{ margin: 0, padding: '0.75rem 0.25rem', color: 'var(--text-muted)' }}
          >
            No models match your search.
          </Text>
        ) : searching ? (
          matches.map((m) => (
            <ModelOption
              key={m.productId}
              label={m.displayName || m.productId}
              meta={modelMetaLine(m)}
              active={selectedId === m.productId}
              onClick={() => onSelect(m.productId)}
            />
          ))
        ) : (
          groups.map((section) => (
            <div key={section.lab}>
              <LabLabel>{section.label}</LabLabel>
              {section.models.map((m) => (
                <ModelOption
                  key={m.productId}
                  label={m.displayName || m.productId}
                  meta={
                    section.models.length > 1
                      ? m.wayLabel || modelMetaLine(m)
                      : modelMetaLine(m)
                  }
                  active={selectedId === m.productId}
                  onClick={() => onSelect(m.productId)}
                />
              ))}
            </div>
          ))
        )}
      </List>
    </Body>
  );
};

export default ModelPicker;
